import { AITextControlPanelAdapter } from './controls/AITextControlPanelAdapter';
import { ColorControl } from './controls/ColorControl';
import { FontControl } from './controls/FontControl';
import { HeadshotControl } from './controls/HeadshotControl';
import { ImageControl } from './controls/ImageControl';
import { NumberControl } from './controls/NumberControl';
import { SpeakerControl } from './controls/SpeakerControl';
import { TextControl } from './controls/TextControl';
import { VideoControl } from './controls/VideoControl';

import type { AITextPanelContext } from './controls/AITextControlPanelAdapter';
import type { ControlConfiguration } from './types';
import type { Speaker } from '@src/components/content-components/SpeakerTypes';
import type { ElementState } from '@src/renderer/ElementState';

interface Props {
  control: ControlConfiguration;
  activeElement: ElementState | null | undefined;
  modifyProperty: (control: ControlConfiguration, value: unknown) => void;
  previewProperty?: (control: ControlConfiguration, value: unknown) => void;
  handleSpeakerControlChange: (
    controlBindings: Record<string, string>,
    inputValues: Speaker, // This could be other things in the future, but for now we're only dealing with speakers
  ) => void;
  convertToVh: (px: string | null, elementId?: string) => string;
  // Inverse of convertToVh — resolves a stored font_size back to px so
  // NumberControl can display it (see ControlRenderProps).
  convertToPx?: (
    value: string | number | null,
    elementId?: string,
  ) => number | null;
  compositionMeta?: Record<string, unknown>;
  onImageScaleChange?: (control: ControlConfiguration, value: unknown) => void;
  /**
   * PLAT-10920 — the generation wiring for `aiText` controls, supplied by the
   * mounting panel. Omitted by surfaces that own their own renderer and editor
   * (the Quote Card panel), where an `aiText` control renders as the plain
   * hand-editable text field it writes to.
   */
  aiTextContext?: AITextPanelContext;
}
export const CompositionControl = ({
  control,
  activeElement,
  modifyProperty,
  previewProperty,
  handleSpeakerControlChange,
  convertToVh,
  convertToPx,
  compositionMeta,
  onImageScaleChange,
  aiTextContext,
}: Props) => {
  if (!activeElement) return null;

  switch (control.type) {
    case 'speaker':
      return (
        <SpeakerControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          onSpeakerControlChange={handleSpeakerControlChange}
          compositionMeta={compositionMeta}
        />
      );
    case 'text':
      return (
        <TextControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          compositionMeta={compositionMeta}
        />
      );
    /*
       PLAT-10920 — the whole of "add the control to Creator Studio": anything
       rendering composition controls picks the type up from here (E1). The
       adapter needs the surface's generation, so without one the control
       degrades to its text field — the same degradation the quick-edit popover
       takes on a surface that passes no `aiTextContext`. A control with no `id`
       takes the same path: the provenance blob both surfaces read is keyed by
       it, so an id-less control could never remember a run.
    */
    case 'aiText':
      return aiTextContext && control.id ? (
        <AITextControlPanelAdapter
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          compositionMeta={compositionMeta}
          context={aiTextContext}
        />
      ) : (
        <TextControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          compositionMeta={compositionMeta}
        />
      );
    case 'color':
      return (
        <ColorControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          previewProperty={previewProperty}
          compositionMeta={compositionMeta}
        />
      );
    case 'number':
      return (
        <NumberControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          compositionMeta={compositionMeta}
          convertToVh={convertToVh}
          convertToPx={convertToPx}
        />
      );
    case 'font':
      return (
        <FontControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          compositionMeta={compositionMeta}
        />
      );
    case 'image':
      return (
        <ImageControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          isLogoControl={true}
          compositionMeta={compositionMeta}
          onScaleChange={onImageScaleChange}
        />
      );
    case 'logo':
      return (
        <ImageControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          isLogoControl={true}
          compositionMeta={compositionMeta}
        />
      );
    case 'video':
      return (
        <VideoControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          compositionMeta={compositionMeta}
        />
      );
    case 'headshot':
      return (
        <HeadshotControl
          control={control}
          activeElement={activeElement}
          modifyProperty={modifyProperty}
          compositionMeta={compositionMeta}
        />
      );
    default:
      return null;
  }
};
